import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaCheck } from "react-icons/fa";
import { toast } from "react-toastify";

import {
  getTMUsers,
  getSalesUsers,
  assignUserToTM,
  updateTeamSales,
  getAssignedTeams,
} from "../../services/teamService";

import "./AddTeamLayer.css";

/* ================= HELPERS ================= */
const getTMNameById = (tmUsers, id) =>
  tmUsers.find((u) => String(u.id) === String(id))?.name || "Unknown TM";

const getSalesNamesByIds = (salesUsers, ids) =>
  salesUsers
    .filter((u) => ids.includes(String(u.id)))
    .map((u) => u.name)
    .join(", ");

const AddTeamLayer = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEdit = Boolean(id);

  const [tmUsers, setTmUsers] = useState([]);
  const [salesUsers, setSalesUsers] = useState([]);
  const [filteredSales, setFilteredSales] = useState([]);

  const [tmId, setTmId] = useState("");
  const [salesIds, setSalesIds] = useState([]);
  const [search, setSearch] = useState("");

  /* ================= LOAD DATA ================= */
  useEffect(() => {
    loadTM();
    loadSales();
    if (isEdit) loadTeam();
  }, [id]);

  const loadTM = async () => {
    const res = await getTMUsers();
    if (res?.status === "success") setTmUsers(res.data || []);
  };

  const loadSales = async () => {
    const res = await getSalesUsers();
    if (res?.status === "success") {
      setSalesUsers(res.data || []);
      setFilteredSales(res.data || []);
    }
  };

  const loadTeam = async () => {
    const res = await getAssignedTeams(id);
    if (res?.status === "success") {
      setTmId(String(res.data.tm_id));
      setSalesIds(res.data.sales.map((s) => String(s.id)));
    }
  };

  /* ================= SEARCH ================= */
  useEffect(() => {
    const f = salesUsers.filter(
      (u) =>
        u.name.toLowerCase().includes(search.toLowerCase()) ||
        u.email.toLowerCase().includes(search.toLowerCase())
    );
    setFilteredSales(f);
  }, [search, salesUsers]);

  /* ================= TOGGLE ================= */
  const toggleSales = (sid) => {
    setSalesIds((prev) =>
      prev.includes(sid)
        ? prev.filter((x) => x !== sid)
        : [...prev, sid]
    );
  };

  /* ================= SUBMIT ================= */
  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!tmId || salesIds.length === 0) {
      toast.error("Please select Team Manager and Sales Executives");
      return;
    }

    const payload = {
      tm_id: tmId,
      sales_ids: salesIds,
    };

    const res = isEdit
      ? await updateTeamSales(payload)
      : await assignUserToTM(payload);

    /* ===== SUCCESS ===== */
    if (res?.status === "success") {
      toast.success(res.message || "Assignment successful");
      navigate("/assign-list");
      return;
    }

    /* ===== ALREADY ASSIGNED (NAME BASED ERROR) ===== */
    if (
      res?.status === "error" &&
      (res.code === "ALREADY_ASSIGNED" ||
        res.message?.toLowerCase().includes("already"))
    ) {
      const tmName = getTMNameById(tmUsers, tmId);
      const salesNames = getSalesNamesByIds(salesUsers, salesIds);

      toast.error(
        `Sales (${salesNames}) are already assigned to Team Manager (${tmName})`
      );
      return;
    }

    /* ===== FALLBACK ===== */
    toast.error(res?.message || "Assignment failed");
  };

  /* ================= RENDER ================= */
  return (
    <div className="add-user-wrapper">
      <div className="add-user-card">
        <h5>
          {isEdit
            ? "Update Sales to Team Manager"
            : "Assign Sales to Team Manager"}
        </h5>

        <form onSubmit={handleSubmit}>
          <div className="grid">

            {/* TEAM MANAGER */}
            <div className="form-group">
              <label>Team Manager</label>
              <select value={tmId} onChange={(e) => setTmId(e.target.value)}>
                <option value="">Select Team Manager</option>
                {tmUsers.map((u) => (
                  <option key={u.id} value={u.id}>
                    {u.name}
                  </option>
                ))}
              </select>
            </div>

            {/* SALES EXECUTIVES */}
            <div className="form-group">
              <label>Sales Executives</label>

              <input
                type="text"
                placeholder="Search sales..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="search-input"
              />

              <div className="sales-select-box">
                {filteredSales.map((u) => {
                  const selected = salesIds.includes(String(u.id));
                  return (
                    <div
                      key={u.id}
                      className={`sales-item ${selected ? "selected" : ""}`}
                      onClick={() => toggleSales(String(u.id))}
                    >
                      <span>{u.name}</span>
                      {selected && <FaCheck className="check-icon" />}
                    </div>
                  );
                })}
              </div>
            </div>

          </div>

          <button className="btn-primary">
            {isEdit ? "Update" : "Assign"}
          </button>
        </form>
      </div>
    </div>
  );
};

export default AddTeamLayer;
